import { createSignal, onCleanup } from 'solid-js'
import { Portal } from 'solid-js/web'
import type { JSX, Accessor } from 'solid-js'
import { afterRender } from '../utils'

/*
	A <textarea> overlaid on a selected text object while it is being edited. It is
	positioned over the live <text> element, but its own box and typography are fixed:
	the editor always reads the same regardless of the object's font, size, colour or
	decoration (a 6pt white-on-black condensed underlined title would be unusable to
	type into).
*/

const EDITOR_WIDTH  = 250;
const EDITOR_HEIGHT = 60;

interface ITextEditOverlay {
	textRef?:     () => SVGTextElement | null | undefined
	svgRef:       Accessor<SVGSVGElement | null>
	initialText?: string
	onDone:       (text: string) => void
}


function clamp(pos: number, extent: number, viewport: number): number {
	return Math.max(0, Math.min(pos, viewport - extent))
}

export function TextEditOverlay(P: ITextEditOverlay): JSX.Element {
	let ta: HTMLTextAreaElement | undefined
	const [style, setStyle] = createSignal<Record<string, string | number>>({})
	let saved = false

	function save(): void {
		if (saved) return
		saved = true
		P.onDone(ta?.value ?? '')
	}

	onCleanup(save)

	// afterRender already defers past mount (the Portal'd textarea and target textEl
	// are attached by then), so no onMount wrapper is needed.
	afterRender(() => {
		const textEl = typeof P.textRef === 'function' ? P.textRef() : null
		if (textEl) {
			const bbox = textEl.getBoundingClientRect()
			setStyle({
				position:          'absolute',
				left:              clamp(bbox.left - 8, EDITOR_WIDTH,  window.innerWidth)  + window.scrollX + 'px',
				top:               clamp(bbox.top  - 4, EDITOR_HEIGHT, window.innerHeight) + window.scrollY + 'px',
				width:             EDITOR_WIDTH  + 'px',
				height:            EDITOR_HEIGHT + 'px',
				padding:           '6px 8px',
				'box-sizing':      'border-box',
				'font-size':       '14px',
				'font-family':     'sans-serif',
				'font-weight':     'normal',
				'font-style':      'normal',
				'text-decoration': 'none',
				'text-transform':  'none',
				'letter-spacing':  'normal',
				'line-height':     '1.4',
				'text-align':      'left',
				color:             '#fff',
				background:        'rgba(0,0,0,.85)',
				border:            '2px solid #4d9eff',
				'border-radius':   '3px',
				outline:           'none',
				resize:            'none',
				'z-index':         99999,
				'white-space':     'pre-wrap',
				'overflow-y':      'auto',
				'overflow-x':      'hidden',
			})
		}
		ta?.focus()
		ta?.select()
	})

	return (
		<Portal>
			<textarea
				ref={el => ta = el}
				data-keeps-selection=''
				style={style() as Record<string, string>}
				value={P.initialText ?? ''}
				onKeyDown={e => {
					if (e.key === 'Escape') { e.preventDefault(); save() }
				}}
				onBlur={save}
			/>
		</Portal>
	)
}
